import Color from "@tiptap/extension-color"
import Highlight from "@tiptap/extension-highlight"
import TaskItem from "@tiptap/extension-task-item"
import TaskList from "@tiptap/extension-task-list"
import TextAlign from "@tiptap/extension-text-align"
import { TextStyle } from "@tiptap/extension-text-style"
import Underline from "@tiptap/extension-underline"
import {
  EditorContent,
  NodeViewContent,
  NodeViewWrapper,
  ReactNodeViewRenderer,
  useEditor,
} from "@tiptap/react"
import type { NodeViewProps } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  CheckSquare,
  Highlighter,
  Italic,
  List,
  ListOrdered,
  TextQuote,
  Underline as UnderlineIcon,
} from "lucide-react"
import { memo, useEffect, useRef, useState } from "react"

import {
  Checkbox,
  cn,
  Field,
  FieldError,
  FieldLabel,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
  Toggle,
} from "@atlant/ui"

const TaskItemComponent = ({ node, updateAttributes }: NodeViewProps) => {
  const checked = node.attrs.checked as boolean
  return (
    <NodeViewWrapper as="li" className="my-0.5 flex list-none items-start gap-2">
      <Checkbox
        checked={checked}
        onCheckedChange={(val) => updateAttributes({ checked: !!val })}
        className="mt-0.75 shrink-0"
      />
      <NodeViewContent
        className={cn(
          "m-0 min-w-0 flex-1",
          checked && "text-muted-foreground line-through",
        )}
      />
    </NodeViewWrapper>
  )
}

const CustomTaskItem = TaskItem.extend({
  addNodeView() {
    return ReactNodeViewRenderer(TaskItemComponent)
  },
})

const PRESET_COLORS = [
  "#000000",
  "#1a1a2e",
  "#16213e",
  "#0f3460",
  "#533483",
  "#6c757d",
  "#adb5bd",
  "#dee2e6",
  "#ffffff",
  "#2d6a4f",
  "#1e88e5",
  "#7b2d8b",
  "#8b5cf6",
  "#ec4899",
  "#ef4444",
  "#f97316",
]

const HIGHLIGHT_COLORS = [
  { color: "#bbf7d0", label: "Green" },
  { color: "#bfdbfe", label: "Blue" },
  { color: "#f5d0fe", label: "Purple" },
  { color: "#fecdd3", label: "Pink" },
  { color: "#fef08a", label: "Yellow" },
]

const ColorPicker = memo(function ColorPicker({
  currentColor,
  onColorChange,
}: {
  currentColor: string
  onColorChange: (color: string) => void
}) {
  const [open, setOpen] = useState(false)
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Text color"
          className="hover:bg-accent mx-1 flex h-8 w-8 items-center justify-center rounded-sm focus:outline-none"
        >
          <span
            className="border-border/50 h-4 w-4 rounded-full border shadow-sm"
            style={{ backgroundColor: currentColor }}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-3" align="start" side="bottom">
        <div className="grid grid-cols-9 gap-1">
          {PRESET_COLORS.map((color) => (
            <button
              key={color}
              type="button"
              aria-label={color}
              onClick={() => {
                onColorChange(color)
                setOpen(false)
              }}
              className={cn(
                "focus:ring-ring h-5 w-5 rounded-full border transition-transform hover:scale-110 focus:ring-2 focus:ring-offset-1 focus:outline-none",
                currentColor === color
                  ? "border-primary ring-primary ring-2 ring-offset-1"
                  : "border-border/40",
              )}
              style={{ backgroundColor: color }}
            />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
})

const HighlightPicker = memo(function HighlightPicker({
  currentHighlight,
  onHighlight,
}: {
  currentHighlight: string | null
  onHighlight: (color: string | null) => void
}) {
  const [open, setOpen] = useState(false)
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Highlight color"
          className={cn(
            "relative flex h-8 w-8 items-center justify-center rounded-sm transition-colors focus:outline-none",
            currentHighlight ? "bg-accent" : "hover:bg-accent",
          )}
        >
          <Highlighter className="h-4 w-4" />
          <span
            className="absolute bottom-1 left-1/2 h-1 w-4 -translate-x-1/2 rounded-full transition-colors"
            style={{ backgroundColor: currentHighlight ?? "transparent" }}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-2" align="start" side="bottom">
        <div className="flex items-center gap-1.5">
          {HIGHLIGHT_COLORS.map(({ color, label }) => (
            <button
              key={color}
              type="button"
              aria-label={label}
              onClick={() => {
                onHighlight(color)
                setOpen(false)
              }}
              className={cn(
                "h-6 w-6 rounded-full border-2 transition-transform hover:scale-110 focus:outline-none",
                currentHighlight === color ? "border-primary" : "border-transparent",
              )}
              style={{ backgroundColor: color }}
            />
          ))}
          <button
            type="button"
            aria-label="Remove highlight"
            onClick={() => {
              onHighlight(null)
              setOpen(false)
            }}
            className="border-border/40 text-muted-foreground hover:bg-accent flex h-6 w-6 items-center justify-center rounded-full border-2 transition-colors focus:outline-none"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </PopoverContent>
    </Popover>
  )
})

const ListPicker = memo(function ListPicker({
  isBullet,
  isOrdered,
  isTask,
  onBullet,
  onOrdered,
  onTask,
}: {
  isBullet: boolean
  isOrdered: boolean
  isTask: boolean
  onBullet: () => void
  onOrdered: () => void
  onTask: () => void
}) {
  const [open, setOpen] = useState(false)
  const isAnyActive = isBullet || isOrdered || isTask
  const activeIcon = isOrdered ? (
    <ListOrdered className="h-4 w-4" />
  ) : isTask ? (
    <CheckSquare className="h-4 w-4" />
  ) : (
    <List className="h-4 w-4" />
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="List type"
          className={cn(
            "flex h-8 items-center gap-0.5 rounded-sm px-1.5 transition-colors focus:outline-none",
            isAnyActive ? "bg-accent" : "hover:bg-accent",
          )}
        >
          {activeIcon}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-44 p-1" align="start" side="bottom">
        {[
          {
            label: "Bullet List",
            icon: <List className="h-4 w-4" />,
            active: isBullet,
            action: onBullet,
          },
          {
            label: "Ordered List",
            icon: <ListOrdered className="h-4 w-4" />,
            active: isOrdered,
            action: onOrdered,
          },
          {
            label: "Task List",
            icon: <CheckSquare className="h-4 w-4" />,
            active: isTask,
            action: onTask,
          },
        ].map(({ label, icon, active, action }) => (
          <button
            key={label}
            type="button"
            onClick={() => {
              action()
              setOpen(false)
            }}
            className={cn(
              "hover:bg-accent flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm",
              active && "bg-accent",
            )}
          >
            {icon} {label}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  )
})

const AlignPicker = memo(function AlignPicker({
  isLeft,
  isCenter,
  isRight,
  onLeft,
  onCenter,
  onRight,
}: {
  isLeft: boolean
  isCenter: boolean
  isRight: boolean
  onLeft: () => void
  onCenter: () => void
  onRight: () => void
}) {
  const [open, setOpen] = useState(false)
  const activeIcon = isCenter ? (
    <AlignCenter className="h-4 w-4" />
  ) : isRight ? (
    <AlignRight className="h-4 w-4" />
  ) : (
    <AlignLeft className="h-4 w-4" />
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Text alignment"
          className="hover:bg-accent flex h-8 items-center gap-0.5 rounded-sm px-1.5 transition-colors focus:outline-none"
        >
          {activeIcon}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-36 p-1" align="start" side="bottom">
        {[
          {
            label: "Left",
            icon: <AlignLeft className="h-4 w-4" />,
            active: isLeft,
            action: onLeft,
          },
          {
            label: "Center",
            icon: <AlignCenter className="h-4 w-4" />,
            active: isCenter,
            action: onCenter,
          },
          {
            label: "Right",
            icon: <AlignRight className="h-4 w-4" />,
            active: isRight,
            action: onRight,
          },
        ].map(({ label, icon, active, action }) => (
          <button
            key={label}
            type="button"
            onClick={() => {
              action()
              setOpen(false)
            }}
            className={cn(
              "hover:bg-accent flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm",
              active && "bg-accent",
            )}
          >
            {icon} {label}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  )
})

const EditorToolbar = memo(function EditorToolbar({
  editor,
  activeColor,
  activeHighlight,
}: {
  editor: ReturnType<typeof useEditor>
  activeColor: string
  activeHighlight: string | null
}) {
  if (!editor) return null
  return (
    <div
      role="toolbar"
      aria-label="Text formatting"
      className="border-border flex items-center gap-0.5 border-b px-2 py-0.5"
      onMouseDown={(e) => e.preventDefault()}
    >
      <Toggle
        size="sm"
        pressed={editor.isActive("bold")}
        onPressedChange={() => editor.chain().focus().toggleBold().run()}
        aria-label="Bold"
        className="data-[state=on]:bg-accent h-8 w-8 p-0"
      >
        <Bold className="h-4 w-4" />
      </Toggle>
      <Toggle
        size="sm"
        pressed={editor.isActive("italic")}
        onPressedChange={() => editor.chain().focus().toggleItalic().run()}
        aria-label="Italic"
        className="data-[state=on]:bg-accent h-8 w-8 p-0"
      >
        <Italic className="h-4 w-4" />
      </Toggle>
      <Toggle
        size="sm"
        pressed={editor.isActive("underline")}
        onPressedChange={() => editor.chain().focus().toggleUnderline().run()}
        aria-label="Underline"
        className="data-[state=on]:bg-accent h-8 w-8 p-0"
      >
        <UnderlineIcon className="h-4 w-4" />
      </Toggle>
      <HighlightPicker
        currentHighlight={activeHighlight}
        onHighlight={(color) =>
          color === null
            ? editor.chain().focus().unsetHighlight().run()
            : editor.chain().focus().setHighlight({ color }).run()
        }
      />
      <ColorPicker
        currentColor={activeColor}
        onColorChange={(color) => editor.chain().focus().setColor(color).run()}
      />
      <Separator orientation="vertical" className="mx-1 h-5" />
      <AlignPicker
        isLeft={editor.isActive({ textAlign: "left" })}
        isCenter={editor.isActive({ textAlign: "center" })}
        isRight={editor.isActive({ textAlign: "right" })}
        onLeft={() => editor.chain().focus().setTextAlign("left").run()}
        onCenter={() => editor.chain().focus().setTextAlign("center").run()}
        onRight={() => editor.chain().focus().setTextAlign("right").run()}
      />
      <Separator orientation="vertical" className="mx-1 h-5" />
      <Toggle
        size="sm"
        pressed={editor.isActive("blockquote")}
        onPressedChange={() => editor.chain().focus().toggleBlockquote().run()}
        aria-label="Blockquote"
        className="data-[state=on]:bg-accent h-8 w-8 p-0"
      >
        <TextQuote className="h-4 w-4" />
      </Toggle>
      <ListPicker
        isBullet={editor.isActive("bulletList")}
        isOrdered={editor.isActive("orderedList")}
        isTask={editor.isActive("taskList")}
        onBullet={() => editor.chain().focus().toggleBulletList().run()}
        onOrdered={() => editor.chain().focus().toggleOrderedList().run()}
        onTask={() => editor.chain().focus().toggleTaskList().run()}
      />
    </div>
  )
})

function EditorStyles({
  placeholder,
  minHeight,
}: {
  placeholder: string
  minHeight: string
}) {
  return (
    <style>{`
      .ProseMirror p.is-editor-empty:first-child::before {
        content: "${placeholder}";
        color: hsl(var(--muted-foreground));
        pointer-events: none;
        float: left;
        height: 0;
      }
      .ProseMirror { min-height: ${minHeight}; }
      .ProseMirror ul { list-style-type: disc; padding-left: 1.25rem; }
      .ProseMirror ol { list-style-type: decimal; padding-left: 1.25rem; }
      .ProseMirror ul[data-type="taskList"] { list-style: none; padding-left: 0; }
      .ProseMirror ul[data-type="taskList"] li { margin-top: 0.25rem; }
      .ProseMirror blockquote {
        border-left: 3px solid var(--color-chart-indigo);
        padding: 0.375rem 0 0.375rem 1rem;
        margin: 0.5rem 0;
        background-color: hsl(var(--muted) / 0.4);
        border-radius: 0 4px 4px 0;
      }
      .ProseMirror p { margin: 0; }
      .ProseMirror > * + * { margin-top: 0.5rem; }
    `}</style>
  )
}

const useRichTextEditor = (
  value: string | undefined,
  onChange: ((html: string) => void) | undefined,
  onBlur?: () => void,
  disabled?: boolean,
) => {
  const onBlurRef = useRef(onBlur)
  useEffect(() => {
    onBlurRef.current = onBlur
  }, [onBlur])

  const lastSetValue = useRef<string | undefined>(undefined)

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ underline: false }),
      Underline,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      TaskList,
      CustomTaskItem.configure({ nested: true }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content: value ?? "",
    editable: !disabled,
    onUpdate({ editor: e }) {
      const html = e.getHTML()
      lastSetValue.current = html
      onChange?.(html)
    },
    onBlur({ editor: e }) {
      const timer = setTimeout(() => {
        if (!e.isFocused) {
          onBlurRef.current?.()
        }
      }, 150)
      return () => clearTimeout(timer)
    },
    editorProps: {
      attributes: { class: "outline-none" },
    },
  })

  useEffect(() => {
    if (!editor || editor.isDestroyed) return
    editor.setEditable(!disabled)
  }, [editor, disabled])

  useEffect(() => {
    if (!editor || editor.isDestroyed) return
    if (value === undefined) return
    if (value === lastSetValue.current) return
    const current = editor.getHTML()
    const isEmpty = value === "" && current === "<p></p>"
    if (isEmpty || value === current) return
    editor.commands.setContent(value, { emitUpdate: false })
  }, [editor, value])

  const [activeColor, setActiveColor] = useState("#000000")
  const [activeHighlight, setActiveHighlight] = useState<string | null>(null)

  useEffect(() => {
    if (!editor || editor.isDestroyed) return undefined
    const update = () => {
      setActiveColor(editor.getAttributes("textStyle").color || "#000000")
      setActiveHighlight(editor.getAttributes("highlight").color || null)
    }
    editor.on("selectionUpdate", update)
    editor.on("transaction", update)
    return () => {
      editor.off("selectionUpdate", update)
      editor.off("transaction", update)
    }
  }, [editor])

  return { editor, activeColor, activeHighlight }
}

export interface RichTextEditorProps {
  value?: string
  onChange?: (html: string) => void
  placeholder?: string
  className?: string
  minHeight?: string
  maxHeight?: string
  disabled?: boolean
  onBlur?: () => void
}

export const RichTextEditor = memo(function RichTextEditor({
  value,
  onChange,
  onBlur,
  placeholder = "Enter text here ...",
  className,
  minHeight = "120px",
  maxHeight,
  disabled = false,
}: RichTextEditorProps) {
  const { editor, activeColor, activeHighlight } = useRichTextEditor(
    value,
    onChange,
    onBlur,
    disabled,
  )

  return (
    <div
      className={cn(
        "border-input bg-background flex flex-col rounded-lg border shadow-sm",
        className,
      )}
    >
      {!disabled && editor && (
        <EditorToolbar
          editor={editor}
          activeColor={activeColor}
          activeHighlight={activeHighlight}
        />
      )}
      <EditorContent
        editor={editor}
        className="flex-1 overflow-y-auto px-3 py-2.5 text-sm"
        style={{
          minHeight,
          ...(maxHeight ? { maxHeight, overflowY: "auto" } : {}),
        }}
      />
      <EditorStyles placeholder={placeholder} minHeight={minHeight} />
    </div>
  )
})

export interface FormRichTextEditorProps {
  value: string
  onChange: (html: string) => void
  name?: string
  label?: string
  placeholder?: string
  className?: string
  editorClassName?: string
  minHeight?: string
  maxHeight?: string
  disabled?: boolean
  error?: string
  onBlur?: () => void
}

export const FormRichTextEditor = ({
  value,
  onChange,
  name,
  label,
  placeholder = "Start typing...",
  className,
  editorClassName,
  minHeight = "120px",
  maxHeight,
  disabled = false,
  error,
  onBlur,
}: FormRichTextEditorProps) => {
  const id = name ?? label?.toLowerCase().replace(/\s+/g, "-")

  return (
    <Field className={className} data-invalid={error ? true : undefined}>
      {label && (
        <FieldLabel htmlFor={id} className="text-muted-foreground text-sm font-medium">
          {label}
        </FieldLabel>
      )}
      <RichTextEditor
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
        className={editorClassName}
        minHeight={minHeight}
        maxHeight={maxHeight}
        disabled={disabled}
      />
      {error && <FieldError>{error}</FieldError>}
    </Field>
  )
}
